import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowRight, Crown, Play, Plus, Star } from 'lucide-react'
import stage from '../../assets/hero-section.png'
import { TESTIMONIALS } from '../../data/testimonials'
import { useVideoReviews } from '../../hooks/useVideoReviews'
import { heroExit, heroIntro } from '../../animations/gsapAnimations'
import { useGsapContext } from '../../hooks/useGsapReveal'
import Button from '../ui/Button'
import { useSection } from '../../cms/cms-context'
import { cmsIcon } from '../../cms/icons'

/* The opening frame, built to the reference: copy on the left under a lime
   breadcrumb, the car on the right lit from behind by a neon ring, a review
   cluster and a badge riding above it, a floating video card between the
   two, and a strip of four assurances closing the fold. Everything sits on
   black; lime is the only light. */

const STEPS = ['Choose', 'Finance', 'Drive']

/* The two figures under the copy. Held as flat fields rather than as a list,
   because there are exactly two of them and the frame is built for two — a
   third would not fit the panel it sits in. */
const STATS = [
  { key: '1', icon: 'Users', value: '1200', suffix: '+', label: 'Leases approved' },
  { key: '2', icon: 'Award', value: '10', suffix: '+', label: 'Years on the market' },
]

const ASSURANCES = [
  {
    id: 'new',
    content: { icon: 'ShieldCheck', title: 'Brand new only', body: 'Factory-fresh, full warranty' },
  },
  {
    id: 'markup',
    content: { icon: 'BadgePercent', title: 'Lowest markup', body: 'Every bank, in writing' },
  },
  {
    id: 'fast',
    content: { icon: 'Zap', title: 'Fast approval', body: 'One file, 48 hours' },
  },
  {
    id: 'contact',
    content: { icon: 'Headphones', title: 'One contact', body: 'From quote to delivery' },
  },
]

/* Faces for the review stack. Photographs first; the initials of a real
   reviewer stand in for any that fails to load, so the stack never shows a
   torn image. */
const FACES = [
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=facearea&facepad=2.5&w=96&h=96&q=70',
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=facearea&facepad=2.5&w=96&h=96&q=70',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=facearea&facepad=2.5&w=96&h=96&q=70',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=facearea&facepad=2.5&w=96&h=96&q=70',
] as const

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)

function Face({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(false)

  return (
    <span className="grid size-11 place-items-center overflow-hidden rounded-full border-2 border-lime bg-raised text-[12px] font-semibold text-lime ring-2 ring-ink">
      {failed ? (
        initialsOf(name)
      ) : (
        <img
          src={src}
          alt=""
          width={44}
          height={44}
          loading="eager"
          decoding="async"
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      )}
    </span>
  )
}

function Hero() {
  /* Two movements on one scope: the arrival, which plays once, and the
     exit, which is scrubbed against the scroll for as long as the page
     lives. The intro returns the SplitText teardown, so it goes last. */
  const scope = useGsapContext<HTMLElement>((element) => {
    heroExit(element)
    return heroIntro(element)
  })

  /* The clip on the floating card is the first review the console has filed.
     Until one exists the card shows the floor itself rather than a
     placeholder video. */
  const reviews = useVideoReviews()
  const clip = reviews.data?.[0]

  const cms = useSection()
  const steps = cms.list('steps', STEPS)
  const assurances = cms.items(ASSURANCES)

  return (
    <section
      id="top"
      ref={scope}
      className="relative overflow-hidden pt-[112px] pb-12 sm:pt-[128px] lg:pb-16"
    >
      {/* ---------- ambient light ----------
          Lime falling into the black from the upper right, where the badge
          and the ring are, and a faint spill on the left under the copy. */}
      <div
        aria-hidden="true"
        data-hero="light"
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute top-[-10%] right-[-8%] h-[560px] w-[720px] rounded-full bg-[radial-gradient(closest-side,rgb(200_245_66/0.13),transparent)]" />
        <div className="absolute bottom-[8%] left-[-12%] h-[420px] w-[560px] rounded-full bg-[radial-gradient(closest-side,rgb(200_245_66/0.06),transparent)]" />
      </div>

      <div className="relative mx-auto w-full max-w-[1320px] px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:gap-4">
          {/* ---------- copy ---------- */}
          <div data-hero="copy" className="relative z-10 lg:pr-6">
            <p
              data-hero="line"
              {...cms.field('steps')}
              className="flex items-center gap-2.5 font-display text-[15px] font-medium text-lime"
            >
              {steps.map((step, index) => (
                <span key={step} className="flex items-center gap-2.5">
                  {index > 0 ? (
                    <ArrowRight aria-hidden="true" strokeWidth={2} className="size-4" />
                  ) : null}
                  {step}
                </span>
              ))}
            </p>

            <h1
              data-hero="title"
              {...cms.field('title')}
              className="mt-5 max-w-[11ch] font-display text-[clamp(2.9rem,6.6vw,5.9rem)] leading-[1.02] font-bold tracking-[-0.035em] text-platinum"
            >
              {cms.text('title', 'Could this be your next')}{' '}
              <span className="text-lime">{cms.text('titleAccent', 'Drive?')}</span>
            </h1>

            <p
              data-hero="line"
              {...cms.field('lede')}
              className="mt-7 max-w-[42ch] text-[16.5px] leading-relaxed text-steel"
            >
              {cms.text(
                'lede',
                'Now is your chance to drive brand new. Check out the cars on our floor, pick the monthly instalment that fits, and let us handle every bank for you.',
              )}
            </p>

            <div
              data-hero="line"
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Button
                to={cms.text('primaryHref', '/home#cars')}
                size="lg"
                arrow
                magnetic
                {...cms.field('primaryCta')}
              >
                {cms.text('primaryCta', 'Explore the Floor')}
              </Button>
              <Button
                variant="ghost"
                size="lg"
                to={cms.text('secondaryHref', '/financing')}
                arrow
                icon="play"
                {...cms.field('secondaryCta')}
              >
                {cms.text('secondaryCta', 'How It Works')}
              </Button>
            </div>

            {/* ---------- the two numbers ---------- */}
            <dl
              data-hero="line"
              className="mt-12 grid w-full grid-cols-2 items-center gap-x-5 rounded-2xl sm:inline-grid sm:w-auto sm:gap-x-6 border border-line bg-raised/80 px-5 py-5 shadow-card backdrop-blur-md sm:gap-x-8 sm:px-7"
            >
              {STATS.map((stat, index) => {
                const Icon = cmsIcon(cms.text(`stat${stat.key}Icon`, stat.icon))
                const value = cms.text(`stat${stat.key}Value`, stat.value)
                const suffix = cms.text(`stat${stat.key}Suffix`, stat.suffix)
                /* The count-up animation reads the figure off the attribute,
                   so a value somebody typed that is not a number is printed
                   as written and simply does not animate. */
                const counted = Number(value)
                return (
                  <div
                    key={stat.key}
                    {...cms.field(`stat${stat.key}Label`)}
                    className={`flex items-center gap-4 ${
                      index > 0 ? 'border-l border-line pl-6 sm:pl-8' : ''
                    }`}
                  >
                    <span aria-hidden="true" className="icon-ring size-12 shrink-0">
                      <Icon strokeWidth={1.7} className="size-5" />
                    </span>
                    <div>
                      <dd
                        data-hero={Number.isFinite(counted) ? 'count' : 'line'}
                        data-count={Number.isFinite(counted) ? counted : undefined}
                        data-suffix={suffix}
                        className="font-display text-[26px] leading-none font-bold tracking-[-0.03em] text-platinum tabular-nums"
                      >
                        {Number.isFinite(counted) ? counted.toLocaleString('en-US') : value}
                        {suffix}
                      </dd>
                      <dt className="mt-1.5 text-[12.5px] text-steel">
                        {cms.text(`stat${stat.key}Label`, stat.label)}
                      </dt>
                    </div>
                  </div>
                )
              })}
            </dl>
          </div>

          {/* ---------- the stage ---------- */}
          <div data-hero="stage" className="relative h-[440px] sm:h-[540px] lg:h-[640px]">
            {/* --- reviews, top left --- */}
            <div
              data-hero="line"
              className="absolute top-0 left-0 z-20 sm:left-4 lg:left-10"
            >
              <div className="flex items-center">
                <div className="flex -space-x-2.5">
                  {FACES.map((face, index) => (
                    <Face
                      key={face}
                      src={face}
                      name={TESTIMONIALS[index]?.name ?? 'Sultan Motors'}
                    />
                  ))}
                </div>
                <Link
                  to="/home#reviews"
                  aria-label="Read every review"
                  className="-ml-2.5 grid size-11 place-items-center rounded-full bg-lime text-void ring-2 ring-ink transition hover:bg-lime-lift"
                >
                  <Plus aria-hidden="true" strokeWidth={2.4} className="size-4.5" />
                </Link>
              </div>
              <p className="mt-3.5 flex items-baseline gap-2">
                <strong
                  {...cms.field('reviewCount')}
                  className="font-display text-[20px] leading-none font-bold text-lime tabular-nums"
                >
                  {cms.text('reviewCount', '1.2K+')}
                </strong>
                <span
                  {...cms.field('reviewLabel')}
                  className="text-[12px] font-medium tracking-[0.08em] text-platinum uppercase"
                >
                  {cms.text('reviewLabel', 'Customer reviews')}
                </span>
              </p>
              <p className="mt-1.5 flex items-center gap-2">
                <span className="flex gap-0.5 text-lime" aria-hidden="true">
                  {Array.from({ length: 5 }, (_, index) => (
                    <Star key={index} className="size-3.5 fill-current" strokeWidth={0} />
                  ))}
                </span>
                <span {...cms.field('rating')} className="text-[12.5px] text-silver tabular-nums">
                  {cms.text('rating', '4.9 / 5')}
                </span>
              </p>
            </div>

            {/* --- the hand-drawn arrow between the two --- */}
            <svg
              aria-hidden="true"
              viewBox="0 0 120 70"
              className="absolute top-[68px] left-[48%] z-10 hidden w-[110px] text-platinum/80 lg:block"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path data-hero="arrow" d="M6 10c30-8 62 4 86 40" />
              <path data-hero="arrow" d="M78 46l14 4 2-14" />
            </svg>

            {/* --- the badge, top right --- */}
            <div
              data-hero="fade"
              className="absolute top-2 right-0 z-20 hidden animate-float-slow flex-col items-center text-center sm:flex sm:right-2 lg:right-6"
            >
              <span aria-hidden="true" className="relative">
                <Crown
                  strokeWidth={1.6}
                  className="size-7 fill-lime/40 text-lime drop-shadow-[0_0_12px_rgb(200_245_66/0.6)]"
                />
              </span>
              <span
                {...cms.field('badgeLabel')}
                className="mt-1.5 rounded-full border border-lime/25 bg-void/60 px-3 py-1 label-sm text-platinum"
              >
                {cms.text('badgeLabel', 'This month')}
              </span>
              <strong
                {...cms.field('badgeTitle')}
                className="mt-1 font-display text-[40px] leading-[0.9] font-extrabold tracking-[-0.04em] text-lime drop-shadow-[0_0_18px_rgb(200_245_66/0.45)] uppercase sm:text-[46px]"
              >
                {/* Two words on two lines, the way the reference sets it. A
                    badge somebody writes as one word simply takes one line. */}
                {cms
                  .text('badgeTitle', 'Best Deal')
                  .split(' ')
                  .map((word, index) => (
                    <span key={`${word}-${index}`} className="block">
                      {word}
                    </span>
                  ))}
              </strong>
              <Link
                to={cms.text('badgeHref', '/financing')}
                {...cms.field('badgeCta')}
                className="mt-2.5 rounded-md border border-lime/30 bg-void/70 px-3 py-1 text-[11px] font-medium text-platinum transition hover:bg-lime hover:text-void"
              >
                {cms.text('badgeCta', 'See financing options')}
              </Link>

              {/* Confetti — a handful of lime flecks around the badge. */}
              <span aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
                <span className="absolute -top-2 -left-6 size-1.5 rotate-12 rounded-sm bg-lime/70" />
                <span className="absolute top-8 -right-8 size-2 rounded-full bg-lime/60" />
                <span className="absolute bottom-3 -left-9 size-1 rounded-full bg-lime" />
                <span className="absolute -bottom-3 right-2 size-1.5 -rotate-12 rounded-sm bg-lime/60" />
                <span className="absolute top-1/2 -right-12 h-px w-4 rotate-45 bg-lime/50" />
              </span>
            </div>

            {/* --- the car and its ring --- */}
            <div
              data-hero="image"
              className="absolute inset-x-[-6%] top-[18%] bottom-0 sm:inset-x-[-2%] lg:inset-x-[-4%]"
            >
              {/* Angular shards of a shade lighter carbon behind everything —
                  the geometry the reference sets its car against. */}
              <span
                aria-hidden="true"
                className="absolute top-[20%] left-[8%] h-[46%] w-[34%] -rotate-12 rounded-[28px] border border-white/[0.05] bg-white/[0.025]"
              />
              <span
                aria-hidden="true"
                className="absolute top-[12%] right-[6%] h-[52%] w-[38%] rotate-[18deg] rounded-[32px] border border-white/[0.04] bg-white/[0.02]"
              />

              {/* The neon ring, full, under the car. */}
              <div
                aria-hidden="true"
                className="absolute top-[50%] left-1/2 w-[66%] max-w-[560px] -translate-x-1/2 -translate-y-1/2 animate-neon"
              >
                <svg viewBox="0 0 200 200" className="size-full">
                  <defs>
                    <linearGradient id="hero-ring" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#e4ff7a" />
                      <stop offset="55%" stopColor="#c8f542" />
                      <stop offset="100%" stopColor="#9fd41a" />
                    </linearGradient>
                  </defs>
                  <circle
                    data-hero="glow"
                    cx="100"
                    cy="100"
                    r="90"
                    fill="none"
                    stroke="rgb(200 245 66 / 0.16)"
                    strokeWidth="16"
                  />
                  {/* Turned so the stroke draws from twelve o'clock: a static
                      orientation, not a rotation. */}
                  <circle
                    data-hero="ring"
                    cx="100"
                    cy="100"
                    r="90"
                    fill="none"
                    stroke="url(#hero-ring)"
                    strokeWidth="4.5"
                    transform="rotate(-90 100 100)"
                  />
                </svg>
              </div>

              {/* The floor: a pool of lime light the car stands in. */}
              <span
                aria-hidden="true"
                data-hero="glow"
                className="absolute bottom-[2%] left-1/2 h-32 w-[74%] -translate-x-1/2 rounded-full bg-lime/25 blur-3xl"
              />

              {/* The car. A studio plate rather than a cutout, so the frame
                  melts into the black on every side and the plate is pushed
                  dark enough that its own backdrop reads as shadow. */}
              <div className="stage-fade absolute inset-0 overflow-hidden">
                <img
                  src={cms.text('image', '') || stage}
                  alt="A brand new Toyota Land Cruiser on the Sultan Motors floor"
                  {...cms.field('image')}
                  fetchPriority="high"
                  decoding="async"
                  className="absolute top-[55%] left-1/2 w-[204%] max-w-none -translate-x-1/2 -translate-y-[54%] brightness-[0.7] contrast-[1.45] saturate-[0.45] animate-drift"
                />
              </div>

              {/* Lime light thrown back onto the car from the ring. */}
              <span
                aria-hidden="true"
                data-hero="glow"
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(48%_42%_at_50%_48%,rgb(200_245_66/0.38),transparent_72%)] mix-blend-screen"
              />

              {/* The top of the ring again, over the plate, so the arc rises
                  clear of the roofline and the ring reads as passing behind
                  the car rather than under a photograph. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute top-[50%] left-1/2 w-[66%] max-w-[560px] -translate-x-1/2 -translate-y-1/2 animate-neon [mask-image:linear-gradient(to_bottom,#000_0%,#000_26%,transparent_46%)]"
              >
                <svg viewBox="0 0 200 200" className="size-full">
                  <circle
                    data-hero="glow"
                    cx="100"
                    cy="100"
                    r="90"
                    fill="none"
                    stroke="rgb(200 245 66 / 0.2)"
                    strokeWidth="16"
                  />
                  <circle
                    data-hero="ring"
                    cx="100"
                    cy="100"
                    r="90"
                    fill="none"
                    stroke="url(#hero-ring)"
                    strokeWidth="4.5"
                    transform="rotate(-90 100 100)"
                  />
                </svg>
              </div>
            </div>

            {/* --- the floating clip --- */}
            <Link
              to="/home#video-reviews"
              data-hero="fade"
              aria-label="Watch a delivery-day review"
              className="group/clip absolute bottom-[6%] left-0 z-20 hidden w-[196px] animate-float rounded-2xl border border-lime/30 bg-raised/90 p-2 shadow-pop backdrop-blur-md transition hover:border-lime lg:block lg:-left-10 lg:bottom-auto lg:top-[38%]"
            >
              <span className="relative block aspect-video overflow-hidden rounded-xl bg-void">
                <img
                  src={clip ? `https://i.ytimg.com/vi/${clip.youtubeId}/hqdefault.jpg` : stage}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="size-full object-cover opacity-85 transition duration-500 group-hover/clip:scale-[1.05]"
                />
                <span className="absolute inset-0 bg-linear-to-t from-void/70 to-transparent" />
                <span className="absolute top-2.5 left-2.5 grid size-8 place-items-center rounded-full bg-lime text-void shadow-action">
                  <Play strokeWidth={0} fill="currentColor" className="ml-0.5 size-3.5" />
                </span>
              </span>
              <span
                {...cms.field('clipLabel')}
                className="mt-2 block px-1 pb-0.5 text-[12.5px] font-semibold text-platinum"
              >
                {cms.text('clipLabel', 'Watch delivery day')}
              </span>
            </Link>
          </div>
        </div>

        {/* ---------- the assurance strip ---------- */}
        <ul
          className="relative z-10 mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-line bg-line/70 sm:grid-cols-2 lg:mt-12 lg:grid-cols-4"
        >
          {assurances.map((item) => {
            const Icon = cmsIcon(item.content.icon)
            return (
              <li
                key={item.id}
                data-hero="strip"
                {...item.props}
                className="flex items-center gap-4 bg-raised/90 px-6 py-6 backdrop-blur-md transition-colors duration-300 hover:bg-raised"
              >
                <span aria-hidden="true" className="icon-ring size-14 shrink-0">
                  <Icon strokeWidth={1.6} className="size-6" />
                </span>
                <div>
                  <h3 className="font-display text-[16px] font-bold tracking-[-0.02em] text-platinum">
                    {item.content.title}
                  </h3>
                  <p className="mt-1 text-[13px] leading-snug text-steel">{item.content.body}</p>
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      {/* ---------- scroll cue, on the left edge ---------- */}
      <a
        href="#cars"
        data-hero="line"
        aria-label="Scroll down to the cars"
        className="absolute bottom-[26%] left-5 hidden flex-col items-center gap-5 rounded-full border border-lime/70 px-3 pt-5 pb-4 text-center shadow-[0_0_24px_rgb(200_245_66/0.18)] transition hover:bg-lime/10 min-[1480px]:flex"
      >
        <span className="text-[12px] leading-[1.35] font-semibold text-platinum">
          Scroll
          <br />
          Down
        </span>
        <span className="relative block h-12 w-px bg-lime/40">
          <ArrowDown
            aria-hidden="true"
            strokeWidth={1.8}
            className="absolute -bottom-1 left-1/2 size-4 -translate-x-1/2 animate-scroll-cue text-lime"
          />
        </span>
      </a>
    </section>
  )
}

export default Hero
